import { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ENVS, FX, FOREGROUND, PLATE_ASPECT, plateUrl, avatarUrl } from "./wildrushPlates";

// WILD RUSH world — bright-daylight layered environment built from the approved Visual Master plates.
// Layers: plate (sky → distant mountains → playable environment) · clouds · sun haze · water/mist · beacon · foreground cutouts · pollen.
const useAspect = () => {
  const q = "(max-aspect-ratio: 1/1)";
  const [tall, setTall] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const m = window.matchMedia(q); const on = (e) => setTall(e.matches);
    m.addEventListener("change", on); return () => m.removeEventListener("change", on);
  }, []);
  return tall ? "tall" : "wide";
};

const preloaded = new Set();
const preloadAll = (aspect) => {
  if (preloaded.has(aspect)) return; preloaded.add(aspect);
  const run = () => ENVS.forEach((e) => { const i = new Image(); i.src = plateUrl(e, aspect); });
  (window.requestIdleCallback || ((f) => setTimeout(f, 1200)))(run);
};

const loop = (dur, delay = 0) => ({ duration: dur, repeat: Infinity, ease: "easeInOut", delay });

// Plates render with background-size: cover, so plate-percent hotspots are mapped to the visible area.
const useViewportAspect = () => {
  const [v, setV] = useState(() => window.innerWidth / window.innerHeight);
  useEffect(() => { const on = () => setV(window.innerWidth / window.innerHeight); window.addEventListener("resize", on); return () => window.removeEventListener("resize", on); }, []);
  return v;
};
const mapper = (plateAspect, viewAspect) => {
  const fx = viewAspect < plateAspect ? viewAspect / plateAspect : 1;
  const fy = viewAspect > plateAspect ? plateAspect / viewAspect : 1;
  const x = (px) => (px - (1 - fx) * 50) / fx, y = (py) => (py - (1 - fy) * 50) / fy;
  return { x, y, w: (pw) => pw / fx, h: (ph) => ph / fy };
};

// The plate and every hotspot effect share one slow drift so glows/mist stay locked to the painted world.
const Plate = ({ env, aspect, reduce, children }) => {
  const [ready, setReady] = useState(false);
  const src = plateUrl(env, aspect);
  useEffect(() => { setReady(false); const i = new Image(); i.onload = () => { setReady(true); preloadAll(aspect); }; i.src = src; }, [src, aspect]);
  return (
    <motion.div className="absolute inset-0" style={{ scale: 1.04 }} animate={reduce ? {} : { x: ["-1.2%", "1.2%", "-1.2%"], y: ["0.6%", "-0.6%", "0.6%"] }} transition={loop(38)}>
      <div className="absolute inset-0 bg-cover bg-center" aria-hidden style={{ backgroundImage: `url(${plateUrl(env, aspect, true)})`, filter: "blur(14px)" }} />
      <div className="absolute inset-0 bg-cover bg-center transition-opacity duration-700" style={{ backgroundImage: `url(${src})`, opacity: ready ? 1 : 0 }} data-testid="wr-plate" />
      {children}
    </motion.div>
  );
};

const Clouds = () => (
  <div className="absolute inset-x-0 top-0 h-[38%] overflow-hidden" aria-hidden>
    {[[8, 10, 34, 9, 90], [55, 4, 40, 10, 120], [30, 22, 26, 7, 105]].map(([x, y, w, h, dur], i) => (
      <motion.div key={i} className="absolute rounded-full bg-white/[.32] blur-2xl" style={{ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` }}
        animate={{ x: ["-6%", "6%", "-6%"], opacity: [0.7, 1, 0.7] }} transition={loop(dur, i * 7)} />
    ))}
  </div>
);

const Sun = ({ at }) => (
  <motion.div className="absolute h-[70vmax] w-[70vmax] -translate-x-1/2 -translate-y-1/2 rounded-full mix-blend-soft-light" aria-hidden
    style={{ left: `${at[0]}%`, top: `${at[1]}%`, background: "radial-gradient(circle, rgba(255,236,190,.95) 0%, rgba(255,220,150,.35) 30%, transparent 62%)" }}
    animate={{ opacity: [0.75, 1, 0.75] }} transition={loop(9)} />
);

const Mist = ({ at: [x, y, s] }) => (
  <motion.div className="absolute -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/60 blur-2xl" aria-hidden
    style={{ left: `${x}%`, top: `${y}%`, width: `${s}vmin`, height: `${s * 0.7}vmin` }}
    animate={{ opacity: [0.35, 0.7, 0.35], scale: [0.9, 1.15, 0.9], y: ["6%", "-10%", "6%"] }} transition={loop(7)} />
);

const Water = ({ box: [x, y, w, h] }) => (
  <div className="absolute overflow-hidden" aria-hidden style={{ left: `${x}%`, top: `${y}%`, width: `${w}%`, height: `${h}%` }}>
    {[0, 1, 2].map((i) => (
      <motion.span key={i} className="absolute h-[2px] rounded-full bg-white/60 blur-[2px]" style={{ top: `${20 + i * 26}%`, width: `${6 + (i % 3) * 4}%` }}
        animate={{ left: ["-20%", "110%"], opacity: [0, 0.45, 0.45, 0] }} transition={{ duration: 7 + i * 1.5, repeat: Infinity, ease: "linear", delay: i * 1.4 }} />
    ))}
  </div>
);

const Beam = ({ at: [x, y], near }) => (
  <div className="absolute" aria-hidden style={{ left: `${x}%`, top: 0, height: `${y}%` }}>
    <motion.div className="absolute bottom-0 -translate-x-1/2 rounded-full bg-[#9ff4ff]" style={{ width: near ? 8 : 3, height: "100%", boxShadow: near ? "0 0 26px 8px rgba(120,235,255,.55)" : "0 0 10px 3px rgba(120,235,255,.45)" }}
      animate={{ opacity: [0.55, 1, 0.55] }} transition={loop(2.6)} />
    <motion.div className="absolute bottom-0 -translate-x-1/2 translate-y-1/2 rounded-full bg-[#7fe9ff] blur-xl" style={{ width: near ? "18vmin" : "8vmin", height: near ? "18vmin" : "8vmin" }}
      animate={{ opacity: [0.35, 0.8, 0.35], scale: [0.9, 1.15, 0.9] }} transition={loop(2.6)} />
  </div>
);

const FG_STYLE = {
  bough: { src: "/wildrush/fg-bough.webp", cls: "left-[-6%] top-[-4%] w-[46vw] max-w-[520px]", amp: 10, dur: 12 },
  ferns: { src: "/wildrush/fg-ferns.webp", cls: "left-[-6%] bottom-[-3%] w-[52vw] max-w-[560px]", amp: 6, dur: 10 },
  rock:  { src: "/wildrush/fg-rock.webp",  cls: "right-[-8%] bottom-[-4%] w-[48vw] max-w-[520px]", amp: 5, dur: 14 },
};
const Foreground = ({ kind, reduce }) => {
  const f = FG_STYLE[kind];
  return (
    <motion.img src={f.src} alt="" aria-hidden draggable={false} className={`pointer-events-none absolute select-none ${f.cls}`} data-testid={`wr-fg-${kind}`}
      animate={reduce ? {} : { x: [-f.amp, f.amp, -f.amp], y: [f.amp / 2, -f.amp / 2, f.amp / 2] }} transition={loop(f.dur)} />
  );
};

const Pollen = () => [0, 1, 2, 3, 4, 5].map((i) => (
  <motion.span key={i} className="absolute h-1 w-1 rounded-full bg-[#fff3c4]/80 shadow-[0_0_6px_2px_rgba(255,240,190,.5)]" aria-hidden
    style={{ left: `${12 + i * 14}%`, top: `${38 + (i % 3) * 14}%` }}
    animate={{ y: [0, -22, 0], x: [0, i % 2 ? 8 : -8, 0], opacity: [0, 0.9, 0] }} transition={loop(6 + i, i * 0.8)} />
));

const PlayerTag = ({ name, x, y, delay }) => (
  <motion.div className="absolute flex items-center gap-1.5 rounded-full border border-white/20 bg-[#06131a]/65 py-1 pl-1 pr-3 text-[13px] font-medium text-white shadow-[0_2px_8px_rgba(0,0,0,.25)] backdrop-blur" style={{ left: x, top: y }}
    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }} data-testid={`wr-player-${name}`}>
    <img src={avatarUrl(name)} alt="" aria-hidden="true" draggable={false} className="h-5 w-5 rounded-full border border-white/25 object-cover" />{name}
  </motion.div>
);

export const WildRushWorld = ({ env, players = [] }) => {
  const reduce = useReducedMotion();
  const aspect = useAspect();
  const key = ENVS.includes(env) ? env : "forest";
  const fx = FX[key][aspect];
  const m = mapper(PLATE_ASPECT[aspect], useViewportAspect());
  const pt = ([x, y]) => [m.x(x), m.y(y)];
  return (
    <div className="absolute inset-0 overflow-hidden bg-[#7fb7e6]" data-testid={`wr-env-${env}`} data-aspect={aspect}>
      <Plate env={key} aspect={aspect} reduce={reduce}>
        {!reduce && fx.mist?.map(([x, y, s], i) => <Mist key={i} at={[m.x(x), m.y(y), s]} />)}
        {!reduce && fx.water?.map(([x, y, w, h], i) => <Water key={i} box={[m.x(x), m.y(y), m.w(w), m.h(h)]} />)}
        {fx.beacon && <Beam at={pt(fx.beacon)} />}
        {fx.crystal && <Beam at={pt(fx.crystal)} near />}
      </Plate>
      {!reduce && <Clouds />}
      {fx.sun && <Sun at={pt(fx.sun)} />}
      {!reduce && <Pollen />}
      {FOREGROUND[key].map((k) => <Foreground key={k} kind={k} reduce={reduce} />)}
      {players.map((n, i) => <PlayerTag key={n} name={n} x={`${16 + i * 27}%`} y={`${33 + (i % 2) * 8}%`} delay={0.4 + i * 0.3} />)}
      <div className="absolute inset-x-0 bottom-0 h-[46%] bg-gradient-to-t from-[#06131a]/75 via-[#06131a]/30 to-transparent" aria-hidden />
    </div>
  );
};

export default WildRushWorld;
